import React, { Component } from "react";
import axios from "axios";
import Helmet from "react-helmet";
import { apiHost } from "../../api/apiClient";
import { utcToLocal } from "../../utils/dateConvert";
import "../../styles/admin/rawMessageDiagnostics.scss";

class RawMessageDiagnostics extends Component {
  state = { data: null, mobileNumber: "", label: "", version: "v3.0.4", loading: false, saving: false, versionSaving: false, error: "", message: "" };

  componentDidMount() { this.load(); }

  auth = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("token") } });

  load = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const [response, versionResponse] = await Promise.all([
        axios.get(`${apiHost}/api/admin/raw-message-diagnostics`, this.auth()),
        axios.get(`${apiHost}/api/SystemVersion`, this.auth()),
      ]);
      this.setState({ data: response.data, version: versionResponse.data.version || "v3.0.4", loading: false });
    } catch (error) {
      this.setState({ loading: false, error: "Unable to load raw message diagnostics." });
    }
  };

  saveVersion = async (event) => {
    event.preventDefault();
    this.setState({ versionSaving: true, error: "", message: "" });
    try {
      const response = await axios.put(`${apiHost}/api/SystemVersion`, { version: this.state.version }, this.auth());
      this.setState({ version: response.data.version, versionSaving: false, message: `AssetTrak Build ${response.data.version} saved.` });
    } catch (error) {
      const detail = error?.response?.data;
      this.setState({ versionSaving: false, error: typeof detail === "string" ? detail : "Unable to save the build version." });
    }
  };

  addRecipient = async (event) => {
    event.preventDefault();
    this.setState({ saving: true, error: "", message: "" });
    try {
      await axios.post(`${apiHost}/api/admin/raw-message-diagnostics/recipients`, {
        mobileNumber: this.state.mobileNumber,
        label: this.state.label,
      }, this.auth());
      this.setState({ mobileNumber: "", label: "", saving: false, message: "SMS recipient added." });
      await this.load();
    } catch (error) {
      const detail = error && error.response && error.response.data;
      this.setState({ saving: false, error: typeof detail === "string" ? detail : "Unable to add SMS recipient." });
    }
  };

  toggleRecipient = async (id) => {
    await axios.put(`${apiHost}/api/admin/raw-message-diagnostics/recipients/${id}/toggle`, {}, this.auth());
    await this.load();
  };

  deleteRecipient = async (id) => {
    if (!window.confirm("Remove this SMS recipient?")) return;
    await axios.delete(`${apiHost}/api/admin/raw-message-diagnostics/recipients/${id}`, this.auth());
    await this.load();
  };

  reset = async () => {
    if (!window.confirm("Reset and re-arm the raw queue SMS alert?")) return;
    await axios.post(`${apiHost}/api/admin/raw-message-diagnostics/reset`, {}, this.auth());
    this.setState({ message: "SMS alert reset and re-armed.", error: "" });
    await this.load();
  };

  formatDate = (value) => value ? (utcToLocal(value) || "—") : "—";

  render() {
    const { data } = this.state;
    const rawCount = data ? data.rawMessageCount : 0;
    const aboveThreshold = data && rawCount > data.threshold;
    return (
      <div className="raw-diagnostics-page">
        <Helmet><title>Raw Message Diagnostics</title></Helmet>
        <section className="raw-diagnostics-hero">
          <div>
            <div className="raw-diagnostics-eyebrow">ADMIN DIAGNOSTICS</div>
            <h1>Raw Message Queue</h1>
            <p>Production queue monitoring and MessageMedia SMS escalation.</p>
          </div>
          <button type="button" className="raw-button raw-button-primary" onClick={this.load} disabled={this.state.loading}>Refresh</button>
        </section>

        {this.state.error && <div className="raw-status raw-status-error">{this.state.error}</div>}
        {this.state.message && <div className="raw-status raw-status-success">{this.state.message}</div>}

        <section className="raw-panel raw-version-panel">
          <div><div className="raw-diagnostics-eyebrow">APPLICATION VERSION</div><h2>Build version</h2><p>Displayed in every user’s account dropdown for this environment.</p></div>
          <form className="raw-version-form" onSubmit={this.saveVersion}>
            <label htmlFor="system-version">Version number</label>
            <div><input id="system-version" value={this.state.version} onChange={(event) => this.setState({ version: event.target.value })} placeholder="v3.0.4" required /><button className="raw-button raw-button-primary" disabled={this.state.versionSaving}>Save version</button></div>
            <small>Dropdown preview: <strong>AssetTrak Build {this.state.version.toLowerCase().startsWith("v") ? this.state.version : `v${this.state.version}`}</strong></small>
          </form>
        </section>

        {data && <>
          <section className="raw-metrics">
            <article className={`raw-metric ${aboveThreshold ? "raw-metric-danger" : "raw-metric-good"}`}>
              <span>Current raw count</span><strong>{rawCount.toLocaleString()}</strong><small>Threshold {data.threshold.toLocaleString()}</small>
            </article>
            <article className="raw-metric"><span>Alert state</span><strong>{data.isArmed ? "Armed" : "Latched"}</strong><small>{data.alertCount} of {data.maximumAlerts} alerts used</small></article>
            <article className="raw-metric"><span>Last checked</span><strong className="raw-metric-date">{this.formatDate(data.lastCheckedAt)}</strong><small>Last result: {data.lastObservedCount.toLocaleString()} messages</small><small>Every {data.intervalMinutes} minutes</small></article>
            <article className="raw-metric"><span>Environment owner</span><strong>{data.environmentOwner}</strong><small>This site: {data.environmentKey}</small></article>
          </section>

          <section className="raw-panel">
            <div className="raw-panel-header"><div><h2>SMS recipients</h2><p>Every enabled recipient receives each alert round.</p></div></div>
            <form className="raw-recipient-form" onSubmit={this.addRecipient}>
              <input value={this.state.mobileNumber} onChange={(e) => this.setState({ mobileNumber: e.target.value })} placeholder="04xx xxx xxx" aria-label="Mobile number" required />
              <input value={this.state.label} onChange={(e) => this.setState({ label: e.target.value })} placeholder="Name or duty role" aria-label="Recipient label" />
              <button className="raw-button raw-button-primary" disabled={this.state.saving}>Add recipient</button>
            </form>
            <div className="raw-table-wrap"><table><thead><tr><th>Mobile</th><th>Label</th><th>Status</th><th>Actions</th></tr></thead><tbody>
              {data.recipients.map((row) => <tr key={row.id}><td>{row.mobileNumber}</td><td>{row.label || "—"}</td><td><span className={`raw-badge ${row.enabled ? "raw-badge-good" : "raw-badge-muted"}`}>{row.enabled ? "Enabled" : "Disabled"}</span></td><td className="raw-actions"><button onClick={() => this.toggleRecipient(row.id)}>{row.enabled ? "Disable" : "Enable"}</button><button className="raw-danger-link" onClick={() => this.deleteRecipient(row.id)}>Remove</button></td></tr>)}
              {!data.recipients.length && <tr><td colSpan="4" className="raw-empty">No SMS recipients configured.</td></tr>}
            </tbody></table></div>
          </section>

          <section className="raw-panel raw-reset-panel"><div><h2>Alert incident</h2><p>After three alert rounds the monitor remains latched until manually reset.</p><div className="raw-detail-grid"><span>First exceeded: {this.formatDate(data.firstExceededAt)}</span><span>Last alert: {this.formatDate(data.lastAlertAt)}</span><span>Last reset: {this.formatDate(data.resetAt)}</span></div>{data.lastError && <div className="raw-status raw-status-error">{data.lastError}</div>}</div><button className="raw-button raw-button-warning" onClick={this.reset}>Reset and re-arm</button></section>

          <section className="raw-panel"><div className="raw-panel-header"><div><h2>SMS alert history</h2><p>The recorded queue count and delivery result for each recipient.</p></div></div><div className="raw-table-wrap"><table><thead><tr><th>Time</th><th>Round</th><th>Raw count</th><th>Mobile</th><th>Delivery</th><th>Provider</th></tr></thead><tbody>
            {data.history.map((row) => <tr key={row.id}><td>{this.formatDate(row.attemptedAt)}</td><td>{row.alertRound} / {data.maximumAlerts}</td><td>{row.rawMessageCount.toLocaleString()}</td><td>{row.mobileNumber}</td><td><span className={`raw-badge ${row.success ? "raw-badge-good" : "raw-badge-danger"}`}>{row.success ? "Sent" : "Failed"}</span></td><td>{row.providerMessage || "—"}</td></tr>)}
            {!data.history.length && <tr><td colSpan="6" className="raw-empty">No SMS alerts have been sent.</td></tr>}
          </tbody></table></div></section>
        </>}
      </div>
    );
  }
}

export default RawMessageDiagnostics;
